import React, { useState } from 'react';
import { Badge } from '../../BasicComponents/Badge';
import type { SyncStatus, SyncStatusBadgeProps } from './types';

// ─── Config ───────────────────────────────────────────────────────────────────

interface StatusConfig {
  variant: 'synced' | 'pendingSync' | 'conflict';
  dot: boolean;
  label: (count: number) => string;
  ariaLabel: (count: number) => string;
}

const STATUS_CONFIG: Record<SyncStatus, StatusConfig> = {
  synced: {
    variant: 'synced',
    dot: true,
    label: () => 'Synced',
    ariaLabel: () => 'All data synced',
  },
  pending: {
    variant: 'pendingSync',
    dot: true,
    label: (n) => `${n} pending`,
    ariaLabel: (n) => `${n} item${n === 1 ? '' : 's'} pending sync`,
  },
  syncing: {
    variant: 'pendingSync',
    dot: false,
    label: () => 'Syncing…',
    ariaLabel: () => 'Syncing data',
  },
  conflict: {
    variant: 'conflict',
    dot: true,
    label: (n) => `${n} conflict${n === 1 ? '' : 's'}`,
    ariaLabel: (n) => `${n} sync conflict${n === 1 ? '' : 's'} need resolution`,
  },
  offline: {
    variant: 'pendingSync',
    dot: true,
    label: () => 'Offline',
    ariaLabel: () => 'Device is offline',
  },
};

// ─── Spinning sync icon ───────────────────────────────────────────────────────

function SpinIcon() {
  return (
    <svg
      className="w-3 h-3 animate-spin shrink-0"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20" strokeDashoffset="8" />
    </svg>
  );
}

// ─── SyncStatusBadge ──────────────────────────────────────────────────────────

export function SyncStatusBadge({
  status = 'synced',
  pendingCount = 0,
  lastSyncTime = null,
  className,
}: SyncStatusBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const config = STATUS_CONFIG[status];
  const label = config.label(pendingCount);
  const ariaLabel = config.ariaLabel(pendingCount);

  const tooltipText = lastSyncTime
    ? `Last synced: ${lastSyncTime.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`
    : null;

  return (
    <span
      className={['relative inline-flex', className ?? ''].filter(Boolean).join(' ')}
      onMouseEnter={() => tooltipText && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => tooltipText && setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      tabIndex={tooltipText ? 0 : undefined}
    >
      <Badge
        variant={config.variant}
        size="sm"
        dot={config.dot && status !== 'syncing'}
        aria-label={ariaLabel}
      >
        {status === 'syncing' && <SpinIcon />}
        {label}
      </Badge>

      {tooltipText && showTooltip && (
        <span
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-10 whitespace-nowrap rounded-md bg-neutral-900 px-2.5 py-1 text-xs text-white shadow-md pointer-events-none"
        >
          {tooltipText}
          <span
            className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-900"
            aria-hidden="true"
          />
        </span>
      )}
    </span>
  );
}
